import React, { useRef } from 'react';
import type useOrderEditing from '../../hooks/useOrderEditing';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import OrderReuseActions from '../reuse/OrderReuseActions';
import RailBillingActions from '../railbilling/RailBillingActions';
import SendHazDocsActions from '../hazmat/SendHazDocsActions';
import OrderDupeActions from '../dupe/OrderDupeActions';

// ---------------------------------------------------------------------------
// SaveBar — the Pro detail footer. Shared between page-mode (renders into the
// DispatchFooter slot) and drawer-mode (renders inline at the bottom of the
// panel). Lives outside ProDetailContainer so it can be exercised on its own.
// ---------------------------------------------------------------------------

export type SaveBarProps = {
  editing: ReturnType<typeof useOrderEditing>;
  onSave: () => void;
  /**
   * Runs the full save flow (deadline/override prompts + validation) and
   * resolves `true` only when it's safe to proceed. Reuse / Undo Reuse await
   * this to enforce save-before-reuse. Same underlying handler as `onSave`,
   * but typed to expose the success signal the reuse actions gate on.
   */
  onSaveBeforeAction: () => Promise<boolean>;
  /** Drawer-only: a Cancel button appears when provided. */
  onClose?: () => void;
  /** True while a delete-eligibility check is running — locks every action and
   *  shows a "Checking…" state so the record can't be saved mid-check. */
  busy?: boolean;
  /** Replaces the generic "Checking…" busy text (e.g. "Uploading documents…"
   *  while a just-created order replays its staged docs). */
  busyLabel?: string;
  /** 'create' = the Create New Order drawer: the button creates the record
   *  ("Create Order", enabled without `hasChanges` — the record itself IS the
   *  change) and the Reuse / Rail Billing / Send Haz Docs / Dupe groups are
   *  suppressed.
   *  Those groups key off `editing.orderDetails`, which EXISTS in create mode
   *  (the seed), so they have to be turned off explicitly. */
  variant?: 'default' | 'create';
  /** What the create button calls the record (#33471). Templates ride the same
   *  create flow as orders, so the noun has to follow the record, not the flow. */
  createNoun?: 'Order' | 'Template';
  /** Change All: fields are flagged "apply to all" whose values the anchor
   *  ALREADY holds, so there is a real Apply pending with no anchor edit behind
   *  it. Enables Save on its own — copying an existing value to the rest of the
   *  set is the most natural way to use Change All, and gating on `hasChanges`
   *  made it look broken. */
  canSaveForFanoutOnly?: boolean;
};

const SaveBar: React.FC<SaveBarProps> = ({
  editing,
  onSave,
  onSaveBeforeAction,
  onClose,
  busy = false,
  busyLabel,
  variant = 'default',
  createNoun = 'Order',
  canSaveForFanoutOnly = false,
}) => {
  const locked = editing.isSaving || busy;
  const isCreate = variant === 'create';
  // Nothing exists to save-then-act on before the order is created, and the
  // record itself is the pending change — so the create button never gates on
  // `hasChanges` and the action groups don't render at all. Each group keeps its
  // own `editing.orderDetails` guard below so the prop still narrows.
  const showActions = !isCreate;
  const canSave = !locked && (isCreate || editing.hasChanges || canSaveForFanoutOnly);
  // V1's footer chords (lib_editrecord.pl: accesskey=s on Save, accesskey=c on
  // Cancel). Each binds only while its button is actually clickable, so Alt+S
  // with nothing to save — or Alt+C in page mode, which has no Close — is a
  // no-op rather than a surprise. Alt+C runs the same handler as the button, so
  // the drawer's unsaved-changes prompt still guards it.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useAltHotkeys(
    {
      S: canSave ? onSave : null,
      C: !locked ? onClose : null,
    },
    rootRef,
  );
  return (
  <div ref={rootRef} className="flex items-center justify-end gap-3 w-full">
    {showActions && editing.orderDetails && (
      <OrderReuseActions
        orderDetails={editing.orderDetails}
        streetTurnOpportunitiesCount={editing.streetTurnOpportunities.length}
        busy={locked}
        hasUnsavedChanges={editing.hasChanges}
        onSaveBeforeAction={onSaveBeforeAction}
        onRefresh={editing.refresh}
      />
    )}
    {showActions && editing.orderDetails && (
      <RailBillingActions
        orderDetails={editing.orderDetails}
        busy={locked}
        onRefresh={editing.refresh}
      />
    )}
    {showActions && editing.orderDetails && (
      <SendHazDocsActions
        orderDetails={editing.orderDetails}
        busy={locked}
        onRefresh={editing.refresh}
      />
    )}
    {showActions && editing.orderDetails && (
      // No onRefresh: a finished dupe navigates to Search Results (closing
      // this drawer), so there is nothing left on screen to refresh.
      <OrderDupeActions
        orderDetails={editing.orderDetails}
        busy={locked}
        hasUnsavedChanges={editing.hasChanges}
        onSaveBeforeAction={onSaveBeforeAction}
      />
    )}
    {onClose && (
      <button
        type="button"
        onClick={onClose}
        disabled={locked}
        title="Close (Alt+C)"
        aria-keyshortcuts="Alt+C"
        className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded hover:bg-slate-100 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 disabled:opacity-50"
      >
        <span className="text-[0.625rem] text-slate-400 dark:text-gray-500 font-medium mr-1">[C]</span>
        Close
      </button>
    )}
    <button
      type="button"
      onClick={onSave}
      disabled={!canSave}
      title={
        isCreate
          ? `Create ${createNoun} (Alt+S)`
          : canSaveForFanoutOnly && !editing.hasChanges
            ? 'Apply the flagged fields to the rest of the set (Alt+S)'
            : 'Save Changes (Alt+S)'
      }
      aria-keyshortcuts="Alt+S"
      className={
        'px-4 py-1.5 text-white text-sm rounded-md transition-colors inline-flex items-center gap-2 ' +
        (locked
          ? 'bg-blue-600 opacity-75 cursor-wait'
          : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed')
      }
    >
      {locked && (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-white" />
      )}
      <span>
        <span className="text-[0.625rem] text-white/70 font-medium mr-1">[S]</span>
        {editing.isSaving
          ? isCreate
            ? 'Creating…'
            : 'Saving…'
          : busy
            ? (busyLabel ?? 'Checking…')
            : isCreate
              ? `Create ${createNoun}`
              : 'Save Changes'}
      </span>
    </button>
  </div>
  );
};

export default SaveBar;
